import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { assetUrl } from "@/lib/assets";

/**
 * AuthGate — a client-side password gate for the static (FTP) admin.
 *
 * Only activates when the build is in static mode. Password is never
 * stored in the bundle; the only artifact is a slow PBKDF2 hash
 * (310,000 iterations, SHA-256) + per-install salt in
 * `/cms-data/admin-config.json`. Brute-forcing is infeasible.
 *
 * On success, a session marker is written to localStorage with a 7-day
 * expiry so the operator isn't re-prompted every page view on the same
 * device.
 */

const SESSION_KEY = "cec-admin-gate-v1";
const SESSION_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

async function pbkdf2Hash(password, saltB64, iterations) {
  const enc = new TextEncoder();
  const salt = Uint8Array.from(atob(saltB64), (c) => c.charCodeAt(0));
  const keyMat = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    keyMat,
    256,
  );
  const bytes = new Uint8Array(bits);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

function hasValidSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.exp === "number" && parsed.exp > Date.now();
  } catch (e) {
    return false;
  }
}

export default function AuthGate({ children }) {
  const [unlocked, setUnlocked] = useState(hasValidSession);
  const [cfg, setCfg] = useState(null);
  const [cfgErr, setCfgErr] = useState(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (unlocked) return;
    const url = `${process.env.PUBLIC_URL || "."}/cms-data/admin-config.json`;
    fetch(url, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => setCfg(data.gate || data))
      .catch((e) => setCfgErr(e.message || String(e)));
  }, [unlocked]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cfg) return;
    setBusy(true);
    setErr("");
    try {
      const hash = await pbkdf2Hash(password, cfg.salt, cfg.iterations);
      if (constantTimeEqual(hash, cfg.hash)) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ exp: Date.now() + SESSION_TTL }));
        setUnlocked(true);
        setPassword("");
      } else {
        setErr("Incorrect password.");
      }
    } catch (ex) {
      setErr("Unable to verify. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-warmwhite" data-testid="admin-auth-gate">
      <form onSubmit={submit} className="w-full max-w-sm">
        <img src={assetUrl("/brand/cec-logo.png")} alt="CEC" className="w-14 h-14 mb-8" />
        <div className="flex items-center gap-2 mb-2 text-[#021D6F]">
          <Lock size={16} />
          <p className="overline">Restricted</p>
        </div>
        <h1 className="h-display text-3xl mb-3">Admin access</h1>
        <p className="text-sm text-graphite/65 mb-8 leading-relaxed">
          This area is private. Enter the admin password to continue.
        </p>

        <label className="block">
          <span className="sr-only">Password</span>
          <input
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3.5 border border-graphite/20 bg-warmwhite outline-none focus:border-[#021D6F] transition-colors"
            placeholder="Password"
            data-testid="admin-gate-password"
            disabled={busy || !!cfgErr}
          />
        </label>

        {err && <p className="mt-3 text-sm text-red-700" data-testid="admin-gate-error">{err}</p>}
        {cfgErr && (
          <p className="mt-3 text-sm text-red-700" data-testid="admin-gate-config-error">
            Admin config missing (<code>/cms-data/admin-config.json</code>).
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !password || !cfg}
          className="mt-5 w-full bg-[#021D6F] text-warmwhite py-3.5 font-medium tracking-wide disabled:opacity-45 hover:bg-[#031a5f] transition-colors"
          data-testid="admin-gate-submit"
        >
          {busy ? "Checking…" : "Enter"}
        </button>

        <p className="mt-10 text-[11px] text-graphite/50 leading-relaxed">
          Access attempts are hashed locally with PBKDF2-SHA-256 (310,000 iterations). Your session is remembered for 7 days on this device.
        </p>
      </form>
    </div>
  );
}
